import useAuthAs from "@raiden/library/hooks/auth/as";
import useCanPerform from "@raiden/library/hooks/useCanPerform";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";
import { isActivePath } from "./MenuItem/MenuItemCore";

/**
 * Trim menu items
 * @param {import("./MenuItem/MenuItemCore").Item[]} menuItems
 * @param {{
 * canPerform: (params: { authorizations: string | string[]}) => boolean,
 * isRoot: boolean
 * }} param1
 * @param {number} [depth]
 * @returns {import("./MenuItem/MenuItemCore").Item[]}
 */
export function prepare(menuItems = [], { canPerform, isRoot }, depth = 0) {
  const trimmed = [];
  menuItems?.forEach((menuItem) => {
    const trimmedChildren = prepare(menuItem.children, { canPerform, isRoot });
    if (
      (menuItem?.validator?.({ canPerform, isRoot }) ?? true) &&
      ((menuItem?.href ?? "")?.length > 0 ||
        typeof menuItem?.onClick === "function" ||
        trimmedChildren.length > 0)
    ) {
      trimmed.push({
        ...menuItem,
        children: prepare(menuItem.children, { canPerform, isRoot }, depth + 1),
      });
    }
  });
  return trimmed;
}

/**
 * @param {object} options
 * @param {import("next/router").NextRouter} options.router
 * @param {string} options.pathWithoutQueryParams
 * @param {import("./MenuItem/MenuItemCore").Item[]} options.menuItems
 * @return {string[]}
 */
function getInitialOpenedMenuItemsIds({
  router,
  pathWithoutQueryParams,
  menuItems = [],
}) {
  /** @type {string[]} */
  const openedMenuItemsIds = [];
  menuItems?.forEach((item) => {
    const isActive = isActivePath({
      isActive: item.isActive,
      router,
      pathWithoutQueryParams,
      href: item.href,
    });
    const isActiveDeep = isActivePath({
      isActive: item.isActive,
      router,
      pathWithoutQueryParams,
      href: item.href,
      children: item.children,
    });
    if ((item.children ?? []).length > 0 && (isActive || isActiveDeep)) {
      openedMenuItemsIds.push(item.id);
    }
  });
  return openedMenuItemsIds;
}

/**
 * @typedef {object} Props
 * @property {import("./MenuItem/MenuItemCore").Item[]} menuItems
 * @property {(params: { menuItems: import("./MenuItem/MenuItemCore").Item[], openedMenuItemsIds: string[], openMenuItem: (item: import("./MenuItem/MenuItemCore").Item) => void, closeMenuItem: (item: import("./MenuItem/MenuItemCore").Item) => void  }) => import("react").ReactElement} render
 *
 * @param {Props} props
 */
function MenuCore({ menuItems: _menuItems, render }) {
  const router = useRouter();

  const canPerform = useCanPerform();

  const { root: isRoot } = useAuthAs();

  const menuItems = useMemo(() => {
    return prepare(_menuItems, { canPerform, isRoot });
  }, [_menuItems, canPerform, isRoot]);

  const [openedMenuItemsIds, setOpenedMenuItemsIds] = useState(() => {
    const pathWithoutQueryParams = router.asPath?.split?.(/[?#]/)[0];
    return getInitialOpenedMenuItemsIds({
      menuItems,
      router,
      pathWithoutQueryParams,
    });
  });

  const openMenuItem = useCallback(
    /** @param {import("./MenuItem/MenuItemCore").Item} item */
    (item) => {
      if (item.depth === 0) {
        setOpenedMenuItemsIds([item.id]);
      } else {
        setOpenedMenuItemsIds((openedMenuItems) => [
          ...openedMenuItems,
          item.id,
        ]);
      }
    },
    [],
  );

  const closeMenuItem = useCallback(
    /** @param {import("./MenuItem/MenuItemCore").Item} item */
    (item) => {
      setOpenedMenuItemsIds((openedMenuItems) => {
        const newOpenedMenuItems = [...openedMenuItems];
        const index = newOpenedMenuItems.indexOf(item.id);
        if (index > -1) {
          newOpenedMenuItems.splice(index, 1);
        }
        return newOpenedMenuItems;
      });
    },
    [],
  );

  return render({ menuItems, openedMenuItemsIds, openMenuItem, closeMenuItem });
}

export default MenuCore;
